import { useAllPlans } from '@features/operations/api/useAllPlans'
import {
  type FrontendPlan,
  getPlanningCffsApi,
  getPlanningCffsQueryKey
} from 'api-validations/locutus'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useQuery } from 'shared-ui/api-provider'
import { groupAndReduce, isNonNil } from 'utils'

export type PlanningClient = {
  name: string
  farms: {
    name: string
    fields: {
      name: string
      id: string
      plans: FrontendPlan[]
    }[]
  }[]
}

type Options = {
  search: string
}

const usePlanningCffItemsQueryOptions = () => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const allPlans = useAllPlans()

  return {
    queryKey: [getPlanningCffsQueryKey, orgCode],
    queryFn: async () => {
      const path = getPlanningCffsApi.pathFor({ orgCode })
      const response = await apiClient.get(path)
      return getPlanningCffsApi.responseSchema.parse(response.body).items
    },
    enabled: isNonNil(allPlans.data)
  }
}

export const usePlanningCffItems = () => useQuery(usePlanningCffItemsQueryOptions())

/**
 * Query for getting CFFs for the Planning section
 *
 * Options:
 * search (string) to filter the results
 */
export const usePlanningCffs = (opts: Options = { search: '' }) => {
  const allPlans = useAllPlans()

  return useQuery({
    ...usePlanningCffItemsQueryOptions(),
    select: (data): { clients: PlanningClient[]; numFields: number } => {
      data = data
        .filter((d) =>
          `${d.client} ${d.farm} ${d.field}`.toLowerCase().includes(opts.search.toLowerCase())
        )
        .sort(
          (a, b) =>
            a.client.localeCompare(b.client) ||
            a.farm.localeCompare(b.farm) ||
            a.field.localeCompare(b.field)
        )

      return {
        clients: groupAndReduce(data, {
          getGroupingKey: ({ client }) => client,
          handleFirst: ({ client, farm, field, id }) => ({
            name: client,
            farms: [
              {
                name: farm,
                fields: [{ name: field, id, plans: allPlans.data?.[id] ?? [] }]
              }
            ]
          }),
          accumulateRest: (acc, { farm, field, id }) => {
            const match = acc.farms.find(({ name }) => name === farm)
            if (!match) {
              acc.farms.push({
                name: farm,
                fields: [{ name: field, id, plans: allPlans.data?.[id] ?? [] }]
              })
              return acc
            }

            match.fields.push({
              name: field,
              id,
              plans: allPlans.data?.[id] ?? []
            })
            return acc
          }
        }),
        numFields: data.length
      }
    }
  })
}
